"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.sortKeySchema = exports.MAX_SORT_KEYS = exports.SORT_DIRECTIONS = void 0;
exports.parseSort = parseSort;
exports.formatSort = formatSort;
exports.toggleSort = toggleSort;
const zod_1 = require("zod");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️ MULTI-COLUMN SORT, ON THE WIRE. Item 45 of the 9 Sep 2026 feedback.    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * *"Click to sort, click to reverse, click to cancel; a second column adds numbered priority."*
 * The server does the ordering — CLAUDE.md §3, *"sorting and filtering are server-side and applied
 * before permission trimming"* — so the client's whole contribution is this string.
 *
 * ⚠️⚠️ **THIS COULD NOT HAVE BEEN BUILT BEFORE THE PAGER WAS.** A keyset cursor carries ONE
 * direction for the whole comparison (`keyset.ts` says so at length): `(a, b) < ($1, $2)` is a
 * single operator over a tuple, and *name ascending, then created descending* is not expressible in
 * it at all. Item 20's offset paging is what makes an arbitrary `order by` payable, and the two
 * items are one change wearing two numbers.
 *
 * ⚠️ **A STRING, not repeated `?sort=` parameters.** The ORDER matters here — it is the priority —
 * and repeated query parameters have no guaranteed order through every proxy and framework in the
 * path. One comma-separated value cannot be reordered by anything.
 */
exports.SORT_DIRECTIONS = ['asc', 'desc'];
/**
 * ⚠️⚠️ **FOUR, AND THE BOUND IS NOT ARBITRARY.** Every key past the first two is a further tiebreak
 * inside a group of rows that already matched, so the fifth changes the order of approximately
 * nothing while costing a sort key Postgres has to carry. It also bounds what a hand-written query
 * string can ask the database to do, which is the half that matters on a public endpoint.
 */
exports.MAX_SORT_KEYS = 4;
/** ⚠️ `[a-z_]` only, so a key can never carry SQL — the catalogue lookup is the real defence. */
const KEY = /^[a-z][a-z0-9_]{0,39}$/u;
exports.sortKeySchema = zod_1.z.object({
    key: zod_1.z.string().regex(KEY),
    direction: zod_1.z.enum(exports.SORT_DIRECTIONS),
});
/**
 * Read `name:asc,created:desc` into keys.
 *
 * ⚠️⚠️ **A MALFORMED ENTRY IS DROPPED, NOT A 400, AND THAT IS `pageSize`'s ARGUMENT AGAIN.** This
 * arrives from a URL somebody may have pasted or edited. An unrecognised key is a list in its
 * default order — visibly not what was asked for — where a refusal is a screen that will not load
 * at all. The server's catalogue drops unknown keys for the same reason one layer down.
 *
 * ⚠️ **Duplicates collapse to the FIRST occurrence**, because a priority list naming one column
 * twice has already said where that column ranks. Keeping the second would silently move it.
 */
function parseSort(raw) {
    if (raw === undefined || raw.trim() === '')
        return [];
    const seen = new Set();
    const keys = [];
    for (const part of raw.split(',')) {
        const [key = '', direction = 'asc'] = part.trim().split(':');
        if (!KEY.test(key) || seen.has(key))
            continue;
        if (direction !== 'asc' && direction !== 'desc')
            continue;
        seen.add(key);
        keys.push({ key, direction });
        if (keys.length === exports.MAX_SORT_KEYS)
            break;
    }
    return keys;
}
/** The inverse. ⚠️ `undefined` for an empty sort, so a caller can drop the parameter entirely. */
function formatSort(keys) {
    if (keys.length === 0)
        return undefined;
    return keys
        .slice(0, exports.MAX_SORT_KEYS)
        .map((sort) => `${sort.key}:${sort.direction}`)
        .join(',');
}
/**
 * What a header click does, given what is sorted now.
 *
 * ⚠️⚠️ **THE NEWEST CLICK BECOMES THE PRIMARY KEY — the product owner's ruling of 9 Sep 2026,
 * against the Django/Unfold convention of appending.** *"Last click ranks highest"*, read as
 * *most important* rather than *highest number*. It matters because the two produce different data:
 * clicking Name then Client sorts by client under this rule and by name under the other.
 *
 * ⚠️ **Three states per column, and the third is OFF rather than back to ascending.** Item 45:
 * *"click to sort, click to reverse, click to cancel"*. A two-state toggle leaves no way to undo a
 * sort except reloading, which is the complaint that produced the item.
 *
 * ⚠️ **Cancelling a key does NOT disturb the others' order** — they keep their relative ranking and
 * simply renumber, because the person cancelling one column did not ask about the rest.
 */
function toggleSort(current, key) {
    const existing = current.find((sort) => sort.key === key);
    const others = current.filter((sort) => sort.key !== key);
    /* Not sorted here yet → it becomes the primary key, ascending. */
    if (existing === undefined) {
        return [{ key, direction: 'asc' }, ...others].slice(0, exports.MAX_SORT_KEYS);
    }
    /* Ascending → descending, and it moves to the front: reversing is a click like any other. */
    if (existing.direction === 'asc') {
        return [{ key, direction: 'desc' }, ...others].slice(0, exports.MAX_SORT_KEYS);
    }
    /* Descending → gone. */
    return others;
}
//# sourceMappingURL=sorting.js.map